//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { IStateOptions } from 'angular-ui-router';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { RouteAnchor } from '@components/RouteAnchor';
import { useCurrentStateData } from '@hooks/useCurrentStateAndParams';
import { NAV_ACTIVE_REGEXES } from '@legacy-components/nav3/navigation-menus/constants/NavRegex';
import { TrackingId } from '@models/TrackingId';
import getKeys from '@utils/getKeys';
import { withTracking } from '@utils/withTracking';

import { IStateData, NavGroup } from 'app/initialization/routing/stateHelpers';
import { RoutableStateName } from 'app/top-level-states';

const calculateActiveRoute = () => {
  const currentPath = window.location.pathname;
  return getKeys(NAV_ACTIVE_REGEXES).find((key) =>
    NAV_ACTIVE_REGEXES[key].test(currentPath),
  );
};

export const getNavGroup = (stateData: IStateData) => {
  return (
    (typeof stateData.navGroup === 'function'
      ? stateData.navGroup()
      : stateData.navGroup) || calculateActiveRoute()
  );
};

interface Props {
  activeNavGroup: NavGroup;
  children: ReactNode;
  state: RoutableStateName;
  stateOptions?: IStateOptions;
  trackingId: TrackingId;
}

export const NavMenuItem = ({
  activeNavGroup,
  children,
  state,
  stateOptions,
  trackingId,
}: Props) => {
  const { stateData } = useCurrentStateData();

  const isActive = getNavGroup(stateData) === activeNavGroup;

  return (
    <NavItem isActive={isActive}>
      <NavItemLink
        isActive={isActive}
        state={state}
        stateOptions={stateOptions}
        stateParams={{ viaTopNav: true }}
        trackingId={trackingId}
      >
        {children}
      </NavItemLink>
    </NavItem>
  );
};

const NavItemLink = withTracking(
  styled(RouteAnchor)<{ isActive: boolean }>(
    ({ isActive, theme }) => `
      background-color: transparent;
      border-radius: ${theme.borderRadii[2]};
      && {
        color: ${isActive ? '#4756c9' : '#333333'}
      }
      font-weight: ${theme.fontWeights.semiBold};
      padding: 2px ${theme.spacings[3]};
      text-decoration: none;
      &&:hover {
        background: #ededed;
        text-decoration: none;
        color: ${isActive ? '#4756c9' : '#333333'}
      }
    `,
  ),
);

const NavItem = styled.div<{ isActive: boolean }>(
  ({ isActive }) => `
    display: flex;
    align-items: center;
    height: 100%;
    ${isActive && `box-shadow: 0 -4px #4756c9 inset;`}
  `,
);
